import { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useAutomationVerification } from '@/hooks/queries/dm-automation';
import sessionStore from '@/store/modules/sessionStore';
import type { DmAutomationDto } from '@/types/dmAutomation';
import type { DmSection } from '@/layouts/AutoDmLayout/Sidebar';
import AutomationListPage from './AutomationListPage';
import SendLogsPage from './SendLogsPage';
import InstagramAccountPage from './InstagramAccountPage';
import AutomationPassPage from './AutomationPassPage';
import HelpPage from './HelpPage';
import AutomationFormPage from './AutomationFormPage';
import VerificationScreen from './VerificationScreen';
import PreviewPanel from './PreviewPanel';
import { ACCENT } from './_shared';

export type AutoDmView = 'list' | 'wizard' | 'detail';

interface Props {
    section: DmSection;
    view: AutoDmView;
    editing: DmAutomationDto | null;
    onChangeView: (view: AutoDmView) => void;
    onChangeSection: (section: DmSection) => void;
    onStartEdit: (automation: DmAutomationDto) => void;
    onExitWizard: () => void;
}

const AutoDmRoot = ({
    section,
    view,
    editing,
    onChangeView,
    onChangeSection,
    onStartEdit,
    onExitWizard,
}: Props) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const sessionUserId = sessionStore.getState().userId;

    // 오토DM 인증 상태(studio) — read-only 로 화면만 분기(판정/차감 금지)
    const verifyQ = useAutomationVerification();
    // 잠금 판정은 studio 의 autodmUnlocked(실효값) 를 따른다.
    // 게이팅 OFF 면 서버가 autodmUnlocked=true 로 내려 인증화면을 안 띄운다(기존 동작).
    // 게이팅 ON + 미인증이면 false → 인증화면. 조회 실패(에러)면 잠그지 않음(서버가 최종 방어).
    const verified = verifyQ.isError ? true : verifyQ.data?.autodmUnlocked === true;

    // 다른 섹션으로 이동 시 선택 해제
    useEffect(() => {
        if (section !== 'list' || view !== 'list') {
            setSelectedId(null);
        }
    }, [section, view]);

    const showPreview = section === 'list' && view === 'list' && verified;

    const renderMain = () => {
        // 인증 게이트 밖(항상 접근 가능): 인스타 연결 · 도움말
        if (section === 'account') return <InstagramAccountPage />;
        if (section === 'help') return <HelpPage />;

        // 인증 게이트: 미인증이면 인증 화면으로 대체 (자동화 만들기/이용권/보낸기록 모두)
        if (verifyQ.isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress sx={{ color: ACCENT }} />
                </Box>
            );
        }
        if (!verified && verifyQ.data) {
            // 인스타 미연동 → 기존 인스타 연동 화면(account 섹션)으로 (연동 기능은 이미 구현돼 있어 재사용)
            // 프로필링크 미생성(드묾, 가입 시 자동 생성) → 기존 스튜디오 편집 화면(/studio/:userId)으로
            return (
                <VerificationScreen
                    verification={verifyQ.data}
                    onGoToInstagram={() => onChangeSection('account')}
                    onGoToProfileLink={
                        sessionUserId ? () => window.open(`/studio/${sessionUserId}`, '_blank') : undefined
                    }
                />
            );
        }

        // 인증됨 → 기존 오토DM 흐름
        if (section === 'list' && view === 'wizard') {
            return (
                <AutomationFormPage
                    key={editing?.id ?? 'new'}
                    automation={editing ?? undefined}
                    onExit={onExitWizard}
                />
            );
        }
        if (section === 'list') {
            return (
                <AutomationListPage
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    onStartWizard={() => onChangeView('wizard')}
                    onGoToAccount={() => onChangeSection('account')}
                    onGoToPass={() => onChangeSection('pass')}
                    onEdit={onStartEdit}
                />
            );
        }
        if (section === 'logs') return <SendLogsPage />;
        if (section === 'pass') return <AutomationPassPage />;
        return null;
    };

    return (
        <>
            <Box
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    overflowY: 'auto',
                    px: { xs: 3, md: 4 },
                    py: 3.5,
                }}
            >
                {renderMain()}
            </Box>

            {showPreview && <PreviewPanel selectedId={selectedId} />}
        </>
    );
};

export default AutoDmRoot;
